import { DestroyRef, inject, Injectable } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Store } from '@ngrx/store';
import { distinctUntilChanged, filter, map, pairwise, startWith, tap } from 'rxjs';
import { AppState } from '../../state/root';
import { selectToken } from '../../state/user/user.selectors';
import { UserActions } from '../../state/user/user.actions';
import { WindowChannelService } from '../window-channel/window-channel.service';
import { WINDOW_CHANNEL_MESSAGE_TYPES } from '../../models/window-channel.model';
import { authSessionId } from '../../models/auth.model';

// Signs out every tab in this browser when one tab signs out. Signing out only clears the shared
// localStorage token, and the JWT isn't revoked server-side, so without this other tabs stay
// signed in on their in-memory token, keep their sockets and keep taking alert locks.
//
// A tab is in a session from the moment it holds a token (including while the user is still
// loading), and broadcasts the id of the session it leaves. A tab still on that session signs out
// when it hears it; matching on the session means a message delivered late, for example to a tab
// the browser had frozen, can't end a session that started after it. Tabs on another session or
// none ignore it, so echoes die out.
@Injectable({
  providedIn: 'root'
})
export class AuthSyncService {
  private store = inject(Store<AppState>);
  private windowChannel = inject(WindowChannelService);
  private destroyRef = inject(DestroyRef);

  private activeSessionId: string | null = null;
  private started = false;

  start(): void {
    if (this.started || !this.windowChannel.isSupported) {
      return;
    }
    this.started = true;

    this.store.select(selectToken).pipe(
      map((authToken) => authToken !== '' ? authSessionId(authToken) : null),
      distinctUntilChanged(),
      tap((sessionId) => {
        this.activeSessionId = sessionId;
      }),
      startWith(null),
      pairwise(),
      filter((pair): pair is [string, string | null] => pair[0] != null),
      takeUntilDestroyed(this.destroyRef),
    ).subscribe(([endedSessionId]) => {
      this.windowChannel.post({ type: WINDOW_CHANNEL_MESSAGE_TYPES.AUTH_SIGNED_OUT, payload: { sessionId: endedSessionId } });
    });

    this.windowChannel.messages(WINDOW_CHANNEL_MESSAGE_TYPES.AUTH_SIGNED_OUT).pipe(
      // Any same-origin script can post on the channel, so a malformed message is ignored rather
      // than allowed to throw and end this subscription.
      map((message) => message.payload?.sessionId),
      filter((sessionId): sessionId is string => this.activeSessionId != null && sessionId === this.activeSessionId),
      takeUntilDestroyed(this.destroyRef),
    ).subscribe((endedSessionId) => {
      this.store.dispatch(UserActions.logout(endedSessionId));
    });
  }
}
